import { useEffect, useMemo, useState } from 'react';
import Card from 'components/card';
import { getSupplierPurchaseStats } from 'interfaces/supplier';

type ProductRow = {
    productId: string;
    name: string;
    ref: string;
    unity: string;
    averageDelayDays: number | null;
    ordersCount: number;
    totalOrderedQuantity: number;
    pendingQuantity: number;
};

type SortKey = 'name' | 'averageDelayDays' | 'pendingQuantity';

const DelayBadge = ({ days, ordersCount }: { days: number | null; ordersCount: number }) => {
    if (days === null) {
        return <span className="text-sm text-gray-400">-</span>;
    }
    const color =
        days <= 3
            ? 'bg-green-100 text-green-800'
            : days <= 10
            ? 'bg-amber-100 text-amber-800'
            : 'bg-red-100 text-red-700';
    return (
        <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${color}`}>
                {days} j
            </span>
            <span className="text-xs text-gray-400">
                ({ordersCount} commande{ordersCount !== 1 ? 's' : ''})
            </span>
        </div>
    );
};

const PendingBadge = ({ qty, unity }: { qty: number; unity: string }) => {
    if (!qty) return <span className="text-sm text-gray-400">-</span>;
    return (
        <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-bold text-orange-700">
            {qty} {unity || ''}
        </span>
    );
};

export default function SupplierProducts(props: { id: string }) {
    const { id } = props;
    const [products, setProducts] = useState<ProductRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('pendingQuantity');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [visibleItems, setVisibleItems] = useState(8);

    const fetchStats = async () => {
        if (!id) return;
        setLoading(true);
        const res = await getSupplierPurchaseStats(id);
        if (res) setProducts(res.products || []);
        setLoading(false);
    };

    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const rows = useMemo(() => {
        let list = products;
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter(
                (p) => p.name?.toLowerCase().includes(q) || p.ref?.toLowerCase().includes(q)
            );
        }
        const sorted = [...list].sort((a, b) => {
            let va: any;
            let vb: any;
            if (sortKey === 'name') {
                va = a.name?.toLowerCase() || '';
                vb = b.name?.toLowerCase() || '';
            } else {
                va = a[sortKey] ?? -1;
                vb = b[sortKey] ?? -1;
            }
            if (va < vb) return sortDir === 'asc' ? -1 : 1;
            if (va > vb) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [products, search, sortKey, sortDir]);

    const SortHeader = ({ label, k }: { label: string; k: SortKey }) => (
        <th
            onClick={() => toggleSort(k)}
            className="cursor-pointer select-none border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30"
        >
            <p className="flex items-center gap-1 text-xs font-bold text-gray-600 dark:text-white">
                {label}
                {sortKey === k && <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
            </p>
        </th>
    );

    return (
        <Card extra={'w-full h-full px-6 pb-6 border'}>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h4 className="text-lg font-medium text-navy-700 dark:text-white">Produits liés</h4>
                    <p className="mt-1 text-sm text-gray-600">
                        {products.length} produit{products.length !== 1 ? 's' : ''} lié{products.length !== 1 ? 's' : ''} à ce fournisseur
                    </p>
                </div>
                {products.length > 0 && (
                    <input
                        type="text"
                        placeholder="Rechercher un produit..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setVisibleItems(8);
                        }}
                        className="w-48 rounded-md border border-gray-200 px-2 py-1 text-sm outline-none focus:border-blue-400 dark:bg-navy-800 dark:text-white"
                    />
                )}
            </div>

            {loading ? (
                <div className="flex h-32 items-center justify-center">
                    <p className="text-gray-400 dark:text-white">Chargement...</p>
                </div>
            ) : rows.length === 0 ? (
                <div className="flex h-32 items-center justify-center">
                    <p className="text-gray-400 dark:text-white">
                        {search ? 'Aucun produit ne correspond à la recherche' : 'Aucun produit lié à ce fournisseur'}
                    </p>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="mt-2 hidden overflow-x-auto md:block">
                        <table className="w-full">
                            <thead>
                                <tr className="!border-px !border-gray-400">
                                    <SortHeader label="Produit" k="name" />
                                    <SortHeader label="Délai moyen de livraison" k="averageDelayDays" />
                                    <SortHeader label="En attente d'arrivage" k="pendingQuantity" />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.slice(0, visibleItems).map((p) => (
                                    <tr key={p.productId} className="hover:bg-gray-50 dark:hover:bg-navy-700">
                                        <td className="max-w-[260px] border-white/0 py-2 px-2">
                                            <p className="truncate text-sm font-medium text-navy-700 dark:text-white" title={p.name}>
                                                {p.name}
                                            </p>
                                            <p className="text-xs text-gray-400">{p.ref || '-'}</p>
                                        </td>
                                        <td className="min-w-[160px] border-white/0 py-2 px-2">
                                            <DelayBadge days={p.averageDelayDays} ordersCount={p.ordersCount} />
                                        </td>
                                        <td className="min-w-[110px] border-white/0 py-2 px-2">
                                            <PendingBadge qty={p.pendingQuantity} unity={p.unity} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="mt-2 space-y-2 md:hidden">
                        {rows.slice(0, visibleItems).map((p) => (
                            <div key={p.productId} className="rounded-xl border border-gray-100 p-3 shadow-sm dark:border-white/10">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">{p.name}</p>
                                        <p className="text-xs text-gray-400">{p.ref || '-'}</p>
                                    </div>
                                    <PendingBadge qty={p.pendingQuantity} unity={p.unity} />
                                </div>
                                <div className="mt-2">
                                    <DelayBadge days={p.averageDelayDays} ordersCount={p.ordersCount} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {rows.length > visibleItems && (
                        <div className="mt-4 flex justify-center">
                            <button
                                onClick={() => setVisibleItems(visibleItems + 10)}
                                className="rounded-lg border border-toola-500 px-4 py-2 text-sm font-medium text-toola-500 transition-colors hover:bg-toola-50"
                            >
                                Voir plus ({rows.length - visibleItems} restants)
                            </button>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}
